import React, { useMemo, useState } from 'react';
import type {
  DxfExtractionDetailsPayload,
  DxfPolylineVolumeLine,
  DxfRawLayer,
} from '../types/dxfExtractionDetails';

function formatDim(n: number, decimals = 2): string {
  if (!Number.isFinite(n)) return '—';
  if (n === 0) return '0';
  if (Math.abs(n) < 1e-8) return '—';
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function formatQty(n: number): string {
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 });
}

export interface DxfExtractionDetailsPanelProps {
  payload: DxfExtractionDetailsPayload;
  processedAt: string;
}

function layerFeatureCount(layer: DxfRawLayer): number {
  return layer.blockInsertCount + layer.polylineCount;
}

function isUnmatched(code: string, unmatched: Set<string>): boolean {
  return unmatched.has(code.trim().toLowerCase());
}

/** In meters: longer in-plan bbox × shorter in-plan bbox × Z extrusion (DXF thickness). */
function polylineSolidDimsMeters(
  row: DxfPolylineVolumeLine,
  linearScaleToMeter: number
): { lengthM: number; widthM: number; heightM: number } {
  const sx = Number(row.planExtentXDrawingUnits) * linearScaleToMeter;
  const sy = Number(row.planExtentYDrawingUnits) * linearScaleToMeter;
  const areaM2 = Number(row.planAreaSquareMeters);
  const heightM = Number(row.thicknessDrawingUnits) * linearScaleToMeter;

  if (!Number.isFinite(sx) || !Number.isFinite(sy)) {
    return { lengthM: Number.NaN, widthM: Number.NaN, heightM };
  }
  // Stored activity JSON before extents existed: keys missing → parsed as NaN above.
  // Degenerate data: both extents zero but solid has area — cannot show L/W.
  if (sx === 0 && sy === 0 && areaM2 > 1e-12) {
    return { lengthM: Number.NaN, widthM: Number.NaN, heightM };
  }

  return {
    lengthM: Math.max(sx, sy),
    widthM: Math.min(sx, sy),
    heightM,
  };
}

const DxfExtractionDetailsPanel: React.FC<DxfExtractionDetailsPanelProps> = ({
  payload,
  processedAt,
}) => {
  const { mappingSummary, rawLayers, mergedLines, linearScaleToMeter, fileName } = payload;

  const unmatchedSet = useMemo(() => {
    const s = new Set<string>();
    for (const c of mappingSummary.unmatchedLayerCodes) {
      s.add(c.trim().toLowerCase());
    }
    return s;
  }, [mappingSummary.unmatchedLayerCodes]);

  const totals = useMemo(() => {
    let vol = 0;
    let len = 0;
    for (const L of rawLayers) {
      vol += Number(L.polylineVolumeSum);
      len += Number(L.lineLengthSum);
    }
    return { volume: vol, length: len };
  }, [rawLayers]);

  const [expandedCodes, setExpandedCodes] = useState<Set<string>>(new Set());

  const toggleLayer = (code: string) => {
    setExpandedCodes((prev) => {
      const next = new Set(prev);
      const key = code.toLowerCase();
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const extracted = mappingSummary.extractedCount;
  const matched = mappingSummary.matchedCount;
  const pct =
    extracted > 0 ? Math.round((matched / extracted) * 1000) / 10 : matched === 0 && extracted === 0 ? 100 : 0;
  const allMatched = mappingSummary.unmatchedCount === 0 && extracted > 0;
  const noLayersExtracted = extracted === 0;

  const processedLabel = useMemo(() => {
    try {
      const d = new Date(processedAt);
      return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return processedAt;
    }
  }, [processedAt]);

  return (
    <div className="space-y-6 pt-1">
      {/* Header + mapping summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-elevation-dark p-5 rounded-xl border border-border-dark relative overflow-hidden">
          <div className="absolute top-0 right-0 p-3 opacity-[0.08] pointer-events-none">
            <span className="material-symbols-outlined text-7xl text-slate-400">description</span>
          </div>
          <div className="relative flex flex-col gap-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-2 py-0.5 bg-primary/15 text-primary text-[10px] font-bold uppercase tracking-widest rounded">
                  DXF import
                </span>
                {noLayersExtracted ? (
                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-widest">No layer codes</span>
                ) : allMatched ? (
                  <span className="flex items-center gap-1 text-emerald-400 text-[10px] font-bold uppercase tracking-widest">
                    <span className="material-symbols-outlined text-xs" style={{ fontVariationSettings: "'FILL' 1" }}>
                      check_circle
                    </span>
                    {pct}% codes matched
                  </span>
                ) : (
                  <span className="flex items-center gap-1 text-amber-400 text-[10px] font-bold uppercase tracking-widest">
                    <span className="material-symbols-outlined text-xs">warning</span>
                    {mappingSummary.unmatchedCount} unmatched layer
                    {mappingSummary.unmatchedCount === 1 ? '' : 's'}
                  </span>
                )}
              </div>
              <h2 className="text-xl sm:text-2xl font-bold tracking-tight text-white mb-1 break-all">{fileName}</h2>
              <p className="text-slate-500 text-sm">Processed: {processedLabel}</p>
              {linearScaleToMeter !== 1 && (
                <p className="text-slate-500 text-xs mt-1">
                  Linear scale: 1 drawing unit = {linearScaleToMeter} m
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-elevation-dark p-5 rounded-xl border border-border-dark flex flex-col justify-between">
          <h3 className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-4">Mapping summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-end gap-4">
              <div>
                <span className="block text-2xl font-bold text-white leading-none">{extracted}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Extracted</span>
              </div>
              <div className="text-right">
                <span className="block text-2xl font-bold text-emerald-400 leading-none">{matched}</span>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Matched</span>
              </div>
            </div>
            <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 transition-all"
                style={{ width: `${extracted > 0 ? Math.min(100, (matched / extracted) * 100) : 0}%` }}
              />
            </div>
            <div className="flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
              <span className="text-slate-500">Efficiency</span>
              <span className="text-emerald-400">{extracted > 0 ? `${pct}%` : '—'}</span>
            </div>
            <p className="text-[10px] text-slate-500">
              Estimate lines: {mappingSummary.estimateLineCount}
            </p>
            {mappingSummary.unmatchedLayerCodes.length > 0 && (
              <div className="pt-2 border-t border-border-dark/60">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Unmatched codes</p>
                <p className="text-xs text-amber-200/90 break-words">
                  {mappingSummary.unmatchedLayerCodes.join(', ')}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Raw geometry layers */}
      <section className="bg-elevation-dark rounded-xl border border-border-dark overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-border-dark flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-slate-900/30">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-6 bg-primary rounded-full shrink-0" />
            <h3 className="text-base font-bold text-white">Raw geometry layers</h3>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Extracted DXF structure</span>
        </div>

        <div className="hidden md:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/50">
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 w-10"> </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">DxfCode</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-right">
                  Count
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-right">
                  Volume (m³)
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-right">
                  Length (m)
                </th>
                <th className="px-6 py-3 w-10" />
              </tr>
            </thead>
            <tbody className="divide-y divide-border-dark/30">
              {rawLayers.map((layer) => {
                const code = layer.dxfCode;
                const key = code.toLowerCase();
                const expanded = expandedCodes.has(key);
                const hasBreakdown = layer.polylineVolumeBreakdown.length > 0;
                const unmatched = isUnmatched(code, unmatchedSet);
                return (
                  <React.Fragment key={code}>
                    <tr
                      className={`hover:bg-white/5 transition-colors group ${
                        hasBreakdown ? 'cursor-pointer' : ''
                      } ${expanded ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                      onClick={() => hasBreakdown && toggleLayer(code)}
                    >
                      <td className="px-6 py-3">
                        <span
                          className={`w-2 h-2 rounded-full inline-block ${
                            unmatched ? 'bg-amber-400' : 'bg-emerald-400'
                          }`}
                          title={unmatched ? 'Unmatched' : 'Matched'}
                        />
                      </td>
                      <td className="px-6 py-3 font-bold text-sm text-white">{code}</td>
                      <td className="px-6 py-3 text-sm text-right font-mono">{formatQty(layerFeatureCount(layer))}</td>
                      <td className="px-6 py-3 text-sm text-right font-mono">
                        {layer.polylineVolumeSum > 0 ? formatDim(layer.polylineVolumeSum) : '—'}
                      </td>
                      <td className="px-6 py-3 text-sm text-right font-mono">
                        {layer.lineLengthSum > 0 ? formatDim(layer.lineLengthSum) : '—'}
                      </td>
                      <td className="px-6 py-3">
                        {hasBreakdown ? (
                          <span
                            className={`material-symbols-outlined text-sm ${
                              expanded ? 'text-primary' : 'text-slate-500 group-hover:text-primary'
                            }`}
                          >
                            {expanded ? 'expand_less' : 'expand_more'}
                          </span>
                        ) : null}
                      </td>
                    </tr>
                    {expanded && hasBreakdown && (
                      <tr className="bg-slate-950/40 border-l-2 border-primary/40">
                        <td className="p-0" colSpan={6}>
                          <div className="px-8 sm:px-12 py-4 space-y-2 overflow-x-auto">
                            <h4 className="text-[10px] font-bold uppercase tracking-widest text-primary mb-3">
                              Polyline volume breakdown
                            </h4>
                            <div className="grid grid-cols-5 gap-2 sm:gap-3 pb-2 border-b border-border-dark/50 text-[10px] font-bold uppercase text-slate-500 min-w-[520px]">
                              <span>Sequence</span>
                              <span className="text-right">Length (m)</span>
                              <span className="text-right">Width (m)</span>
                              <span className="text-right" title="DXF entity thickness (extrusion along Z)">
                                Height (m)
                              </span>
                              <span className="text-right">Volume (m³)</span>
                            </div>
                            {layer.polylineVolumeBreakdown.map((row) => {
                              const seq = String(row.sequenceIndex + 1).padStart(3, '0');
                              const { lengthM, widthM, heightM } = polylineSolidDimsMeters(row, linearScaleToMeter);
                              return (
                                <div
                                  key={`${code}-${row.sequenceIndex}`}
                                  className="grid grid-cols-5 gap-2 sm:gap-3 py-2 text-xs font-mono text-slate-400 hover:text-slate-200 min-w-[520px]"
                                >
                                  <span>{seq}</span>
                                  <span className="text-right">{formatDim(lengthM)}</span>
                                  <span className="text-right">{formatDim(widthM)}</span>
                                  <span className="text-right">{formatDim(heightM)}</span>
                                  <span className="text-right">{formatDim(row.volumeCubicMeters)}</span>
                                </div>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-primary/5">
                <td className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-primary text-right" colSpan={3}>
                  Global totals
                </td>
                <td className="px-6 py-3 text-base font-bold text-white text-right font-mono">
                  {formatDim(totals.volume)}
                </td>
                <td className="px-6 py-3 text-base font-bold text-white text-right font-mono">
                  {formatDim(totals.length)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile cards */}
        <div className="md:hidden divide-y divide-border-dark/30">
          {rawLayers.map((layer) => {
            const code = layer.dxfCode;
            const key = code.toLowerCase();
            const expanded = expandedCodes.has(key);
            const hasBreakdown = layer.polylineVolumeBreakdown.length > 0;
            const unmatched = isUnmatched(code, unmatchedSet);
            return (
              <div
                key={`m-${code}`}
                className={`p-4 ${expanded ? 'bg-primary/5 border-l-2 border-primary' : 'bg-elevation-dark'}`}
              >
                <button
                  type="button"
                  className="w-full flex justify-between items-start mb-2 text-left"
                  onClick={() => hasBreakdown && toggleLayer(code)}
                  disabled={!hasBreakdown}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className={`w-2 h-2 rounded-full shrink-0 ${unmatched ? 'bg-amber-400' : 'bg-emerald-400'}`}
                    />
                    <h4 className="font-bold text-white truncate">{code}</h4>
                  </div>
                  {hasBreakdown ? (
                    <span className={`material-symbols-outlined shrink-0 ${expanded ? 'text-primary' : 'text-slate-500'}`}>
                      {expanded ? 'expand_less' : 'expand_more'}
                    </span>
                  ) : null}
                </button>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-500">Count</p>
                    <p className="text-sm font-mono text-white">{formatQty(layerFeatureCount(layer))}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-bold uppercase text-slate-500">Volume (m³)</p>
                    <p className="text-sm font-mono text-white">
                      {layer.polylineVolumeSum > 0 ? formatDim(layer.polylineVolumeSum) : '—'}
                    </p>
                  </div>
                </div>
                {expanded && hasBreakdown && (
                  <div className="mt-2 bg-slate-950/50 p-3 rounded-lg space-y-2 border border-border-dark/40">
                    <h5 className="text-[10px] font-bold uppercase text-primary">Polyline volume breakdown</h5>
                    <div className="space-y-2 text-[11px] font-mono border-t border-border-dark/30 pt-2">
                      {layer.polylineVolumeBreakdown.map((row) => {
                        const seq = String(row.sequenceIndex + 1).padStart(3, '0');
                        const { lengthM, widthM, heightM } = polylineSolidDimsMeters(row, linearScaleToMeter);
                        return (
                          <div
                            key={`m-${code}-${row.sequenceIndex}`}
                            className="flex justify-between gap-2 text-slate-400 items-start"
                          >
                            <span className="text-white shrink-0">{seq}</span>
                            <span className="text-right flex flex-col items-end gap-1 min-w-0">
                              <span>
                                L {formatDim(lengthM)} × W {formatDim(widthM)} × H {formatDim(heightM)} m
                              </span>
                              <span className="text-[10px] text-slate-500">
                                Volume {formatDim(row.volumeCubicMeters)} m³
                              </span>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          <div className="p-4 bg-primary/10 flex justify-between items-center">
            <span className="text-[10px] font-bold uppercase text-primary">Global volume (m³)</span>
            <span className="text-lg font-bold text-white font-mono">{formatDim(totals.volume)}</span>
          </div>
        </div>
      </section>

      {/* Merged estimates */}
      <section className="bg-elevation-dark rounded-xl border border-border-dark overflow-hidden">
        <div className="px-4 sm:px-6 py-3 border-b border-border-dark flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 bg-slate-900/30">
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-6 bg-emerald-500 rounded-full shrink-0" />
            <h3 className="text-base font-bold text-white">Merged estimates</h3>
          </div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500">Mapped bill quantities</span>
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/50">
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">Item code</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500">Source</th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-widest text-slate-500 text-right">
                  Quantity
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border-dark/30">
              {mergedLines.map((line, idx) => (
                <tr key={`${line.itemCode}-${line.sourceDxfCode}-${idx}`} className="hover:bg-white/5 transition-colors">
                  <td className="px-6 py-3 font-bold text-sm text-white">{line.itemCode}</td>
                  <td className="px-6 py-3 text-xs text-slate-400">{line.sourceDxfCode}</td>
                  <td className="px-6 py-3 text-sm text-right font-mono">{formatQty(line.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="sm:hidden divide-y divide-border-dark/30">
          {mergedLines.map((line, idx) => (
            <div key={`${line.itemCode}-${line.sourceDxfCode}-${idx}`} className="p-4 space-y-2">
              <div className="flex justify-between items-center gap-2">
                <span className="font-bold text-sm text-white truncate">{line.itemCode}</span>
                <span className="px-2 py-0.5 bg-slate-800 text-[10px] rounded text-slate-400 shrink-0 max-w-[40%] truncate">
                  {line.sourceDxfCode}
                </span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-[10px] font-bold uppercase text-slate-500">Qty</span>
                <span className="font-mono text-sm text-emerald-400">{formatQty(line.quantity)}</span>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default DxfExtractionDetailsPanel;
